import axios from 'axios';
import { keycloak } from '../auth';

const FILE_SHARE_URL = import.meta.env.VITE_FILE_SHARE_URL || '';

export const fileShareApi = axios.create({
  baseURL: FILE_SHARE_URL,
  timeout: 30_000,
});

// Keycloak token interceptor (same pattern as ipezby/tasker)
fileShareApi.interceptors.request.use(
  async (config) => {
    if (keycloak.token) {
      try {
        await keycloak.updateToken(30);
      } catch {
        keycloak.login();
        return Promise.reject(new Error('Token refresh failed'));
      }
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

fileShareApi.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error),
);

// Types
export interface FileEntry {
  name: string;
  is_dir: boolean;
  size: number;
  mod_time: string;
}

export interface LinkToken {
  token: string;
  path: string;
  created_by: string;
  created_by_name: string;
  created_at: string;
  expires_at: string;
  max_downloads: number;
  download_count: number;
  note: string;
}

export interface CreateLinkRequest {
  path: string;
  ttl_minutes: number;
  max_downloads: number;
  note: string;
}

export interface CreateLinkResponse {
  token: string;
  url: string;
  expires_at: string;
  max_downloads: number;
}

export interface AuditEvent {
  id: number;
  token: string;
  event: string;
  actor: string;
  remote_ip: string;
  user_agent: string;
  meta: string;
  created_at: string;
}

export function parseUserAgent(ua: string): string {
  if (!ua) return 'Неизвестно';
  let browser = '';
  let os = '';
  if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Linux/.test(ua)) os = 'Linux';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/YaBrowser/.test(ua)) browser = 'Yandex';
  else if (/SamsungBrowser/.test(ua)) browser = 'Samsung';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  if (browser && os) return `${browser}, ${os}`;
  return browser || os || ua.substring(0, 40);
}

export const fileShare = {
  isConfigured: () => !!FILE_SHARE_URL,

  listFiles: (path: string) =>
    fileShareApi.get<{ path: string; entries: FileEntry[] }>('/api/files', { params: { path } }).then(r => r.data),

  createLink: (data: CreateLinkRequest) =>
    fileShareApi.post<CreateLinkResponse>('/api/links', data).then(r => r.data),

  listLinks: () =>
    fileShareApi.get<{ links: LinkToken[] }>('/api/links').then(r => r.data),

  deleteLink: (token: string) =>
    fileShareApi.delete(`/api/links/${token}`),

  getAuditEvents: (token: string) =>
    fileShareApi.get<{ events: AuditEvent[] }>('/api/audit', { params: { token, limit: 100 } }).then(r => r.data),
};
